import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { BookOutlined, InfoCircleOutlined, ToolOutlined } from "@ant-design/icons";
import { AutoComplete, Button, Col, Popover, Row, Space } from "antd";
import { useCallback, useEffect, useMemo, useState } from "react";
import { Trans } from "@devowl-wp/react-frontend";
import { useTranslation } from "../../../../../../../others/i18n.js";
import { spacing } from "../../../../../../../styles/spacing.js";
const renderTitle = (title, isCustom = false)=>/*#__PURE__*/ _jsxs(Space, {
        style: {
            display: "flex",
            justifyContent: "left"
        },
        children: [
            /*#__PURE__*/ _jsx("span", {
                children: isCustom ? /*#__PURE__*/ _jsx(ToolOutlined, {}) : /*#__PURE__*/ _jsx(BookOutlined, {})
            }),
            title
        ]
    });
const QuestionSelection = ({ add, purposeOptions })=>{
    const { t } = useTranslation();
    const addItemRef = useCallback(add, [
        add
    ]);
    // Save actual selected Question for "Add" button.
    const [selectedQuestion, setSelectedQuestion] = useState();
    // Prepare a Memo of translated questions
    const questionsMap = useMemo(()=>purposeOptions ? purposeOptions.map((option)=>{
            return {
                questionKey: option.questionKey,
                translation: option.question
            };
        }) : [], [
        purposeOptions
    ]);
    // questions and custom questions for option building
    const [customQuestions, setCustomQuestions] = useState([]);
    const [questions, setQuestions] = useState(questionsMap);
    // build options for autocomplete
    const [customQuestionOptions, setCustomQuestionOptions] = useState();
    useEffect(()=>{
        setCustomQuestionOptions([
            ...customQuestions.map((customQuestion)=>{
                return {
                    value: customQuestion,
                    key: customQuestion
                };
            })
        ]);
    }, [
        customQuestions
    ]);
    const [questionOptions, setQuestionOptions] = useState();
    useEffect(()=>{
        setQuestionOptions([
            ...questions.map((question)=>{
                return {
                    value: question.translation,
                    key: question.questionKey
                };
            })
        ]);
    }, [
        questions
    ]);
    // finally compose to options for autocomplete field
    const [options, setOptions] = useState();
    useEffect(()=>{
        const preparedOptions = [];
        if (customQuestionOptions && customQuestionOptions.length > 0) {
            preparedOptions.push({
                label: renderTitle(t("Custom Questions"), true),
                options: customQuestionOptions
            });
        }
        if (questionOptions && questionOptions.length > 0) {
            preparedOptions.push({
                label: renderTitle(t("Predefined Questions")),
                options: questionOptions
            });
        }
        setOptions(preparedOptions);
    }, [
        customQuestionOptions,
        questionOptions
    ]);
    // search term to filter entries (only predefined!)
    const [searchTerm, setSearchTerm] = useState("");
    useEffect(()=>{
        if (searchTerm && searchTerm.trim() !== "") {
            const searchCi = searchTerm.toLowerCase();
            // filter predefined options!
            setQuestions(questionsMap.filter((question)=>question.translation.toLowerCase().includes(searchCi)));
        // custom questions will remain the same
        } else {
            setQuestions(questionsMap);
        }
    }, [
        searchTerm
    ]);
    return /*#__PURE__*/ _jsxs(Row, {
        style: {
            display: "flex",
            justifyContent: "space-between",
            marginBottom: spacing.layout.content.padding
        },
        children: [
            /*#__PURE__*/ _jsx(Col, {
                flex: 9,
                children: /*#__PURE__*/ _jsx(AutoComplete, {
                    options: options,
                    onSelect: (selection, option)=>{
                        setSelectedQuestion(option["key"]);
                    },
                    onChange: (selected)=>{
                        const found = questionsMap.find((entry)=>entry.translation === selected);
                        const question = found ? found.questionKey : selected;
                        setSelectedQuestion(question);
                    },
                    onSearch: (search)=>{
                        setSearchTerm(search);
                    },
                    placeholder: t("Questions ..."),
                    allowClear: true
                })
            }),
            /*#__PURE__*/ _jsxs(Col, {
                flex: 1,
                style: {
                    textAlign: "right"
                },
                children: [
                    /*#__PURE__*/ _jsx(Popover, {
                        placement: "left",
                        title: t("Purpose variables"),
                        content: /*#__PURE__*/ _jsxs(Trans, {
                            children: [
                                "The following variables can be used in ",
                                /*#__PURE__*/ _jsx("strong", {
                                    children: "purpose answers"
                                }),
                                " of the service template. They get automatically replaced with the appropriate value. Samples:",
                                /*#__PURE__*/ _jsx("br", {}),
                                /*#__PURE__*/ _jsx("br", {}),
                                /*#__PURE__*/ _jsxs("code", {
                                    children: [
                                        /*#__PURE__*/ _jsxs("span", {
                                            children: [
                                                " ",
                                                "{{"
                                            ]
                                        }),
                                        "template.name",
                                        /*#__PURE__*/ _jsxs("span", {
                                            children: [
                                                "}}",
                                                " "
                                            ]
                                        })
                                    ]
                                }),
                                /*#__PURE__*/ _jsx("br", {}),
                                /*#__PURE__*/ _jsxs("code", {
                                    children: [
                                        /*#__PURE__*/ _jsxs("span", {
                                            children: [
                                                " ",
                                                "{{"
                                            ]
                                        }),
                                        "template.providerPrivacyPolicyUrl",
                                        /*#__PURE__*/ _jsxs("span", {
                                            children: [
                                                "}}",
                                                " "
                                            ]
                                        })
                                    ]
                                }),
                                /*#__PURE__*/ _jsx("br", {}),
                                /*#__PURE__*/ _jsxs("code", {
                                    children: [
                                        /*#__PURE__*/ _jsxs("span", {
                                            children: [
                                                " ",
                                                "{{"
                                            ]
                                        }),
                                        "template.providerLegalNoticeUrl",
                                        /*#__PURE__*/ _jsxs("span", {
                                            children: [
                                                "}}",
                                                " "
                                            ]
                                        })
                                    ]
                                }),
                                /*#__PURE__*/ _jsx("br", {}),
                                /*#__PURE__*/ _jsxs("code", {
                                    children: [
                                        /*#__PURE__*/ _jsxs("span", {
                                            children: [
                                                " ",
                                                "{{"
                                            ]
                                        }),
                                        "template.provider.name",
                                        /*#__PURE__*/ _jsxs("span", {
                                            children: [
                                                "}}",
                                                " "
                                            ]
                                        })
                                    ]
                                })
                            ]
                        }),
                        children: /*#__PURE__*/ _jsx(Button, {
                            icon: /*#__PURE__*/ _jsx(InfoCircleOutlined, {}),
                            type: "link",
                            children: t("Variables")
                        })
                    }),
                    /*#__PURE__*/ _jsx(Button, {
                        onClick: ()=>{
                            const trimmedQuestion = selectedQuestion?.trim();
                            const defaultAnswer = purposeOptions.find((option)=>option.questionKey === trimmedQuestion)?.defaultAnswer;
                            addItemRef({
                                isNewParagraph: false,
                                question: trimmedQuestion,
                                answer: defaultAnswer
                            });
                            if (purposeOptions.findIndex((option)=>option.questionKey === trimmedQuestion) < 0) {
                                setCustomQuestions([
                                    ...customQuestions,
                                    trimmedQuestion
                                ]);
                            }
                        },
                        disabled: selectedQuestion === undefined || selectedQuestion.trim() === "",
                        children: t("Add")
                    })
                ]
            })
        ]
    });
};
export { QuestionSelection };
